import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useIntegrations } from 'contexts/IntegrationsContext';
import { useAgent } from 'contexts/AgentContext';
import { Center, Spinner, Text, Box, VStack, Icon, Button } from '@chakra-ui/react';
import { CheckCircleIcon, WarningIcon } from '@chakra-ui/icons';
import AgentSelectionInline from 'components/agent/AgentSelectionInline';

// Flow states for the OAuth callback process
const FLOW_STATES = {
  LOADING: 'loading',           // Initial load and validation
  AGENT_SELECTION: 'selection', // Show agent picker
  CONNECTING: 'connecting',     // Cloud function executing
  SUCCESS: 'success',          // Integration created successfully
  ERROR: 'error'               // Something failed
};

export default function OAuthCallback() {
  const { platformId } = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { connect, platforms } = useIntegrations();
  const { agents } = useAgent();
  
  const [flowState, setFlowState] = useState(FLOW_STATES.LOADING);
  const [oauthCode, setOauthCode] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  // Extract platform ID from URL - handle both /oauth/:platformId/callback and /integrations/:platformId/create
  const actualPlatformId = platformId || (() => {
    const pathParts = location.pathname.split('/');
    // For /integrations/google-calendar/create, platformId is at index 2
    return pathParts[2];
  })();

  const platform = platforms.find(p => p.id === actualPlatformId);
  const platformName = platform?.displayName || actualPlatformId;

  const handleAgentNext = useCallback(async (agent) => {
    try {
      setSelectedAgent(agent);
      setFlowState(FLOW_STATES.CONNECTING);
      
      console.log('Connecting integration:', { platformId: actualPlatformId, code: oauthCode, agentId: agent.id });
      
      // Call the cloud function and wait for completion
      await connect(actualPlatformId, { code: oauthCode, agentId: agent.id });
      
      console.log('Integration created successfully');
      setFlowState(FLOW_STATES.SUCCESS);
    } catch (error) {
      console.error('Error creating integration:', error);
      setErrorMessage(error.message || 'Failed to create integration. Please try again.');
      setFlowState(FLOW_STATES.ERROR);
    }
  }, [actualPlatformId, oauthCode, connect]);

  const handleCancel = () => {
    // For now, just close the tab when user cancels
    window.close();
  };

  const handleCloseTab = () => {
    window.close();
  };

  useEffect(() => {
    const code = search.get('code');
    console.log('OAuth callback starting, code:', code ? 'present' : 'missing');
    console.log('Available agents:', agents);
    
    if (!code) {
      console.log('No code found, showing error');
      setErrorMessage('No authorization code received from the OAuth provider.');
      setFlowState(FLOW_STATES.ERROR);
      return;
    }

    // Store the OAuth code
    setOauthCode(code);

    // Check if there's a pre-selected agent from the OAuth initiation
    const preSelectedAgentId = sessionStorage.getItem('oauth_agent_id');
    console.log('Pre-selected agent ID:', preSelectedAgentId);

    if (preSelectedAgentId && agents.length > 0) {
      const preSelectedAgent = agents.find(agent => agent.id === preSelectedAgentId);
      if (preSelectedAgent) {
        console.log('Found pre-selected agent:', preSelectedAgent.name);
        // Auto-proceed with the pre-selected agent
        setSelectedAgent(preSelectedAgent);
        // Clear the stored agent ID
        sessionStorage.removeItem('oauth_agent_id');
        // Start connecting immediately
        handleAgentNext(preSelectedAgent);
        return;
      } else {
        console.log('Pre-selected agent not found in available agents');
        // Clear invalid stored agent ID
        sessionStorage.removeItem('oauth_agent_id');
      }
    }

    // No pre-selected agent or agent not found, show selection UI
    setFlowState(FLOW_STATES.AGENT_SELECTION);
  }, [search, agents, handleAgentNext]);

  // Render based on current flow state
  switch (flowState) {
    case FLOW_STATES.LOADING:
      return (
        <Center h="100vh" flexDirection="column">
          <Spinner size="xl" />
          <Box mt="4">
            <Text>Loading...</Text>
          </Box>
        </Center>
      );

    case FLOW_STATES.AGENT_SELECTION:
      return (
        <Center h="100vh" flexDirection="column">
          <AgentSelectionInline
            agents={agents}
            platformName={platformName}
            onNext={handleAgentNext}
            onCancel={handleCancel}
            isLoading={false}
          />
        </Center>
      );

    case FLOW_STATES.CONNECTING:
      return (
        <Center h="100vh" flexDirection="column">
          <VStack spacing="6">
            <Spinner size="xl" />
            <VStack spacing="2" textAlign="center">
              <Text fontSize="xl" fontWeight="bold">
                Connecting...
              </Text>
              <Text fontSize="lg" color="gray.600">
                Connecting {platformName} to {selectedAgent?.name}
              </Text>
              <Text fontSize="sm" color="gray.500">
                Please wait while we set up your integration...
              </Text>
            </VStack>
          </VStack>
        </Center>
      );

    case FLOW_STATES.SUCCESS:
      return (
        <Center h="100vh" flexDirection="column">
          <VStack spacing="6">
            <Icon as={CheckCircleIcon} boxSize="20" color="green.500" />
            <VStack spacing="2" textAlign="center">
              <Text fontSize="2xl" fontWeight="bold" color="green.600">
                Integration Successful!
              </Text>
              <Text fontSize="lg" color="gray.600">
                {platformName} has been connected to {selectedAgent?.name}
              </Text>
              <Text fontSize="sm" color="gray.500" mb="4">
                You can now close this tab or it will close automatically.
              </Text>
              <Button colorScheme="brand" onClick={handleCloseTab}>
                Close Tab
              </Button>
            </VStack>
          </VStack>
        </Center>
      );

    case FLOW_STATES.ERROR:
      return (
        <Center h="100vh" flexDirection="column">
          <VStack spacing="6">
            <Icon as={WarningIcon} boxSize="20" color="red.500" />
            <VStack spacing="2" textAlign="center">
              <Text fontSize="2xl" fontWeight="bold" color="red.600">
                Integration Failed
              </Text>
              <Text fontSize="lg" color="gray.600">
                {errorMessage || `There was an error connecting ${platformName}`}
              </Text>
              <Text fontSize="sm" color="gray.500" mb="4">
                You can close this tab and try again.
              </Text>
              <Button colorScheme="red" variant="outline" onClick={handleCloseTab}>
                Close Tab
              </Button>
            </VStack>
          </VStack>
        </Center>
      );

    default:
      return (
        <Center h="100vh" flexDirection="column">
          <Spinner size="xl" />
          <Box mt="4">
            <Text>Processing...</Text>
          </Box>
        </Center>
      );
  }
}
